import React, { useState } from 'react';
import { Table, Form } from 'react-bootstrap';
import { FaPencilAlt } from 'react-icons/fa';
import { PatientInterface } from '../../../interfaces';
import ConfirmModal from '../../modal/confirm-modal';

interface PatientTableProps {
  patient: PatientInterface;
  onSave: (updatedPatient: PatientInterface) => void;
}

const editableCellStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.25rem 0.5rem',
  borderRadius: '4px',
  border: '1px dashed #dee2e6',
  backgroundColor: '#f8f9fa',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
};

const editableCellHoverStyle: React.CSSProperties = {
  backgroundColor: '#e9ecef',
  borderColor: '#0d6efd',
};

const PatientTable: React.FC<PatientTableProps> = ({ patient, onSave }) => {
  const [editablePatient, setEditablePatient] = useState(patient);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleChange = (field: string, value: string | number | boolean) => {
    setEditablePatient((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBlur = () => {
    setEditingField(null);
    setShowConfirmModal(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmModal(false);
    onSave(editablePatient);
  };

  const handleCancelSave = () => {
    setShowConfirmModal(false);
    setEditablePatient(patient);
  };

  const renderEditableCell = (field: string, value: string | number, suffix?: string) => {
    if (editingField === field) {
      if (field === 'birthDate') {
        return (
          <Form.Control
            style={{ width: 'auto', display: 'inline-block' }}
            type="date"
            value={value as string}
            onChange={(e) => handleChange(field, e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        );
      }
      return (
        <Form.Control
          style={{ width: 'auto', display: 'inline-block' }}
          type="text"
          value={value}
          onChange={(e) => handleChange(field, e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      );
    }
    const isHovered = hoveredField === field;
    return (
      <span
        style={{
          ...editableCellStyle,
          ...(isHovered ? editableCellHoverStyle : {}),
        }}
        onClick={() => setEditingField(field)}
        onMouseEnter={() => setHoveredField(field)}
        onMouseLeave={() => setHoveredField(null)}
        title="Click to edit"
      >
        {value}{suffix}
        <FaPencilAlt size={12} color={isHovered ? '#0d6efd' : '#6c757d'} />
      </span>
    );
  };

  return (<>
    <Table striped bordered hover responsive size="sm">
      <tbody>
        <tr>
          <td>Name</td>
          <td>
            {renderEditableCell('firstName', editablePatient.firstName)}{' '}
            {renderEditableCell('lastName', editablePatient.lastName)}
          </td>
        </tr>
        <tr>
          <td>Email</td>
          <td>{renderEditableCell('email', editablePatient.email)}</td>
        </tr>
        <tr>
          <td>Birthdate</td>
          <td>{renderEditableCell('birthDate', editablePatient.birthDate)}</td>
        </tr>
        <tr>
          <td>Gender</td>
          <td>{editablePatient.gender}</td>
        </tr>
        <tr>
          <td>Height</td>
          <td>{renderEditableCell('height', editablePatient.height, ' cm')}</td>
        </tr>
        <tr>
          <td>Weight</td>
          <td>{renderEditableCell('weight', editablePatient.weight, ' lbs')}</td>
        </tr>
        <tr>
          <td>Address</td>
          <td>
            {`${patient.address.street1} ${patient.address.city} ${patient.address.state} ${patient.address.zip}`}
          </td>
        </tr>
        {patient.voucherApiResponse?.onboarding_url && (
          <tr>
            <td>Onboarding URL</td>
            <td>
              {patient.voucherApiResponse?.onboarding_url}
            </td>
          </tr>
        )}
        <tr>
          <td>Has Paid?</td>
          <td>
            {editingField === 'paid' ? (
              <Form.Select
                style={{ width: 'auto', display: 'inline-block' }}
                value={editablePatient.paid ? 'Yes' : 'No'}
                onChange={(e) =>
                  handleChange('paid', e.target.value === 'Yes')
                }
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                autoFocus
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Form.Select>
            ) : (
              <span
                style={{
                  ...editableCellStyle,
                  ...(hoveredField === 'paid' ? editableCellHoverStyle : {}),
                }}
                onClick={() => setEditingField('paid')}
                onMouseEnter={() => setHoveredField('paid')}
                onMouseLeave={() => setHoveredField(null)}
                title="Click to edit"
              >
                {editablePatient.paid ? 'Yes' : 'No'}
                <FaPencilAlt size={12} color={hoveredField === 'paid' ? '#0d6efd' : '#6c757d'} />
              </span>
            )}
          </td>
        </tr>
        <tr>
          <td>Refunded</td>
          <td>
            {editingField === 'refunded' ? (
              <Form.Select
                style={{ width: 'auto', display: 'inline-block' }}
                value={editablePatient.refunded ? 'Yes' : 'No'}
                onChange={(e) =>
                  handleChange('refunded', e.target.value === 'Yes')
                }
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                autoFocus
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </Form.Select>
            ) : (
              <span
                style={{
                  ...editableCellStyle,
                  ...(hoveredField === 'refunded' ? editableCellHoverStyle : {}),
                }}
                onClick={() => setEditingField('refunded')}
                onMouseEnter={() => setHoveredField('refunded')}
                onMouseLeave={() => setHoveredField(null)}
                title="Click to edit"
              >
                {editablePatient.refunded ? 'Yes' : 'No'}
                <FaPencilAlt size={12} color={hoveredField === 'refunded' ? '#0d6efd' : '#6c757d'} />
              </span>
            )}
          </td>
        </tr>
      </tbody>
    </Table>

    <ConfirmModal
      show={showConfirmModal}
      title="Save Changes"
      message="Do you want to save the changes you made?"
      confirmText="Save Changes"
      cancelText="Discard"
      onConfirm={handleConfirmSave}
      onCancel={handleCancelSave}
    />
  </>);
};

export default PatientTable;
